import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Row, Col, Button, DatePicker, Select, InputNumber, Alert, Spin, Tabs, Tag } from 'antd';
import { CalculatorOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import StorageDetailCharts, { MultiPerspectiveReviewChart, StorageStrategyCompareChart } from '@/components/views/StorageDetailCharts';
import {
  useAgcStorage,
  useAgcStorageReviewStrategies,
  useAgcStorageReviewTheoreticalOptimal,
  useAgcStorageTheoreticalOptimal,
} from '@/api/agc';
import { useStations } from '@/hooks/useStations';
import { format2, sanitizeFilename } from '@/utils/charts';
import type { AgcStorageReviewStrategy, AgcStorageRow, AgcStorageTheoreticalOptimalRow } from '@/types/api';

import { DEFAULT_PENALTY_BASE, DEFAULT_EFF, DEFAULT_CURT_THRES } from '@/config/constants';

const toNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatWindow = (rows: AgcStorageTheoreticalOptimalRow[], powerKey: 'charge_power' | 'discharge_power') => {
  const active = rows.filter((row) => toNumber(row[powerKey]) > 0.001);
  if (!active.length) return '无';
  return `${active[0].period} - ${active[active.length - 1].period}`;
};

const REVIEW_WINDOW_START_MINUTE = 6 * 60;

const absoluteTimeLabel = (dateText: string, timeText: string): string => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(timeText))) return String(timeText);
  const [hourText, minuteText] = String(timeText).split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return `${dateText}T${String(timeText)}`;
  }
  return dayjs(dateText).add(hour, 'hour').add(minute, 'minute').format('YYYY-MM-DDTHH:mm:00');
};

const minuteOfDay = (timeText: string): number | null => {
  const text = String(timeText);
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    const parsed = dayjs(text);
    return parsed.isValid() ? parsed.hour() * 60 + parsed.minute() : null;
  }
  const [hourText, minuteText] = text.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

const buildReviewWindowRows = (
  currentRows: AgcStorageRow[],
  currentDate: string,
  nextRows: AgcStorageRow[],
  nextDate: string,
): AgcStorageRow[] => {
  const current = currentRows
    .filter((row) => (minuteOfDay(row.time) ?? -1) >= REVIEW_WINDOW_START_MINUTE)
    .map((row) => ({ ...row, time: absoluteTimeLabel(currentDate, row.time) }));
  const next = nextRows
    .filter((row) => {
      const minute = minuteOfDay(row.time);
      return minute !== null && minute < REVIEW_WINDOW_START_MINUTE;
    })
    .map((row) => ({ ...row, time: absoluteTimeLabel(nextDate, row.time) }));
  return [...current, ...next];
};

const calculateReviewActualTotals = (rows: AgcStorageRow[]) => {
  let declaredSpreadRevenue = 0;
  let declaredCharge = 0;
  let declaredDischarge = 0;
  let actualSpreadRevenue = 0;
  let actualCharge = 0;
  let actualDischarge = 0;

  for (const row of rows) {
    const ep = toNumber(row.ep);
    const price = toNumber(row.p);
    const fallbackDeclaredDischargeQ = Math.max(0, ep) * (5 / 60);
    const declaredDischargeQ = toNumber(row.declared_discharge_q, fallbackDeclaredDischargeQ);
    const declaredDischargeRevenue = toNumber(row.declared_discharge_revenue, declaredDischargeQ * price);
    const actualChargeQ = toNumber(row.c_q);
    const actualChargeCost = toNumber(row.actual_charge_cost, actualChargeQ * price);

    declaredSpreadRevenue += declaredDischargeRevenue - actualChargeCost;
    declaredCharge += actualChargeQ;
    declaredDischarge += declaredDischargeQ;
    actualSpreadRevenue += toNumber(row.actual_spread_revenue, toNumber(row.e) - toNumber(row.c_q) * price);
    actualCharge += toNumber(row.c_q);
    actualDischarge += toNumber(row.e_q);
  }

  return {
    declaredSpreadRevenue,
    declaredCharge,
    declaredDischarge,
    actualSpreadRevenue,
    actualCharge,
    actualDischarge,
  };
};

const summarizeReviewTheoreticalRows = (rows: AgcStorageTheoreticalOptimalRow[]) => {
  if (!rows.length) return null;
  let chargeEnergy = 0;
  let dischargeEnergy = 0;
  let chargeCost = 0;
  let dischargeRevenue = 0;
  for (const row of rows) {
    chargeEnergy += toNumber(row.charge_energy);
    dischargeEnergy += toNumber(row.discharge_energy);
    chargeCost += toNumber(row.charge_cost);
    dischargeRevenue += toNumber(row.discharge_revenue);
  }
  return {
    chargeEnergy,
    dischargeEnergy,
    netRevenue: dischargeRevenue - chargeCost,
  };
};

const strategyTypeLabel = (type: AgcStorageReviewStrategy['strategy_type']) => {
  if (type === 'ex_ante_review') return '事前复盘策略';
  if (type === 'intraday_dispatch') return '临场调度策略';
  return type;
};

const calculationStatusColor = (status?: string) => {
  if (status === 'complete') return 'green';
  if (status === 'estimated') return 'blue';
  if (status === 'incomplete') return 'red';
  return 'default';
};

const latestReviewStrategy = (strategies: AgcStorageReviewStrategy[]) => (
  strategies.length ? strategies[strategies.length - 1] : null
);

const hasConcreteReviewCalculation = (strategy: AgcStorageReviewStrategy | null) => {
  const netRevenue = strategy?.calculation?.net_revenue;
  return netRevenue !== null && netRevenue !== undefined && Number.isFinite(Number(netRevenue));
};

const compactUnknown = (value: unknown, fallback = '-'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const firstText = (data: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = data[key];
    if (value !== null && value !== undefined && value !== '') return String(value);
  }
  return '';
};

const formatStrategyAction = (action: Record<string, unknown>, index: number) => {
  const method = firstText(action, ['method', 'name', 'description']) || firstText(action, ['type']) || `动作${index + 1}`;
  const start = firstText(action, ['period_start', 'window_start', 'start_time', 'start']);
  const end = firstText(action, ['period_end', 'window_end', 'end_time', 'end']);
  const volume = firstText(action, ['volume_mwh', 'energy_mwh', 'target_energy_mwh']);
  const power = firstText(action, ['power_mw']);
  const parts = [method];
  if (start || end) parts.push(`${start || '?'}-${end || '?'}`);
  if (volume) parts.push(`${volume} MWh`);
  if (power && !volume) parts.push(`${power} MW`);
  return parts.join(' · ');
};

const strategyText = (strategy: AgcStorageReviewStrategy) => {
  const rawText = String(strategy.raw_text || '').trim();
  if (rawText) return rawText;
  if (strategy.actions.length) {
    return strategy.actions.map((action, index) => formatStrategyAction(action, index)).join('；');
  }
  return '策略内容未填写';
};

const strategyMissingItems = (strategy: AgcStorageReviewStrategy) => {
  const calculation = strategy.calculation;
  const items = [
    ...(calculation?.missing_fields ?? []),
    ...strategy.uncertainties,
  ].filter(Boolean);
  if (items.length) return Array.from(new Set(items));
  if (!calculation) return ['待后端收益计算'];
  return ['需要补充可计算策略'];
};

const summarizeReviewStrategies = (
  strategies: AgcStorageReviewStrategy[],
  emptyNote: string,
) => {
  if (!strategies.length) {
    return { value: '待日志上传', note: emptyNote };
  }

  const latest = strategies[strategies.length - 1];
  const calculation = latest.calculation;
  if (!calculation) {
    return { value: `${strategies.length} 条草稿`, note: '待后端收益计算' };
  }

  if (calculation.is_calculable && calculation.net_revenue !== null) {
    return {
      value: `${format2(calculation.net_revenue)} 元`,
      note: `${calculation.status_label} · 充/放 ${format2(calculation.charge_energy)} / ${format2(calculation.discharge_energy)} MWh`,
    };
  }

  const hasPendingSettlement = calculation.missing_fields.some((item) => item.includes('收益待补全'));
  if (hasPendingSettlement) {
    const missingText = calculation.missing_fields.slice(0, 2).join('；');
    const pendingCharge = toNumber(calculation.pending_charge_energy);
    const pendingDischarge = toNumber(calculation.pending_discharge_energy);
    return {
      value: '收益待补全',
      note: `已知部分 ${format2(calculation.known_net_revenue)} 元 · 待补充/放 ${format2(pendingCharge)} / ${format2(pendingDischarge)} MWh · ${missingText}`,
    };
  }

  const missingText = calculation.missing_fields.slice(0, 2).join('；');
  return {
    value: calculation.status_label || '数据不足',
    note: missingText || '需要补充策略字段',
  };
};

const AgcStoragePage: React.FC = () => {
  const { data: stationsData, isLoading: stationsLoading } = useStations();
  const stations = stationsData?.stations ?? [];

  const [selectedDisplay, setSelectedDisplay] = useState('');
  const stationReal = selectedDisplay;

  useEffect(() => {
    if (stations.length > 0 && !selectedDisplay) {
      setSelectedDisplay(stations[0]);
    }
  }, [stations, selectedDisplay]);

  const yesterday = dayjs().subtract(1, 'day');
  const [date, setDate] = useState<dayjs.Dayjs>(yesterday);

  const [penaltyBase, setPenaltyBase] = useState(DEFAULT_PENALTY_BASE);
  const [capMax, _setCapMax] = useState(0);
  const [eff, setEff] = useState(DEFAULT_EFF);
  const [curtThres, setCurtThres] = useState(DEFAULT_CURT_THRES);
  const [showParams, setShowParams] = useState(false);

  const dateStr = date.format('YYYY-MM-DD');
  const nextDateStr = date.add(1, 'day').format('YYYY-MM-DD');

  const params = useMemo(
    () => ({
      station: stationReal,
      date: dateStr,
      penalty_base: penaltyBase,
      cap_max: capMax,
      eff,
      curt_thres: curtThres,
      include_attribution: true,
    }),
    [stationReal, dateStr, penaltyBase, capMax, eff, curtThres]
  );
  const nextDayParams = useMemo(
    () => ({
      ...params,
      date: nextDateStr,
      use_temp_price_fallback: true,
    }),
    [nextDateStr, params]
  );

  const { mutate, data, isPending, error } = useAgcStorage();
  const {
    mutate: mutateNextDay,
    data: nextDayData,
    isPending: isNextDayPending,
    error: nextDayError,
  } = useAgcStorage();
  const {
    mutate: mutateTheoretical,
    data: theoreticalData,
    isPending: isTheoreticalPending,
    error: theoreticalError,
  } = useAgcStorageTheoreticalOptimal();
  const {
    mutate: mutateReviewTheoretical,
    data: reviewTheoreticalData,
    isPending: isReviewTheoreticalPending,
    error: reviewTheoreticalError,
  } = useAgcStorageReviewTheoreticalOptimal();
  const {
    data: reviewStrategiesData,
    isLoading: isReviewStrategiesLoading,
    error: reviewStrategiesError,
    refetch: refetchReviewStrategies,
  } = useAgcStorageReviewStrategies({ station: stationReal, date: dateStr });

  const hasResponse = !!data;
  const hasData = hasResponse && Array.isArray(data.data?.rows) && data.data.rows.length > 0;
  const rows: AgcStorageRow[] = hasData ? data.data.rows : [];
  const mtdAvg = hasResponse ? data.data.mtd_avg : 0;
  const pdiff = hasResponse ? data.data.pdiff : 0;
  const capMaxDb = hasResponse ? data.data.cap_max_db : 0;
  const subsidyPrice = hasResponse ? data.data.subsidy_price : 0;

  const handleCalculate = useCallback(() => {
    mutate(params);
    mutateNextDay(nextDayParams);
    mutateTheoretical({ station: stationReal, date: dateStr });
    mutateReviewTheoretical({ station: stationReal, date: dateStr });
    refetchReviewStrategies();
  }, [
    mutate,
    mutateNextDay,
    mutateReviewTheoretical,
    mutateTheoretical,
    nextDayParams,
    params,
    refetchReviewStrategies,
    stationReal,
    dateStr,
  ]);

  const theoreticalHasResponse = !!theoreticalData;
  const theoreticalSummary = theoreticalData?.data?.summary;
  const theoreticalRows: AgcStorageTheoreticalOptimalRow[] = theoreticalData?.data?.rows ?? [];
  const nextDayRows: AgcStorageRow[] = Array.isArray(nextDayData?.data?.rows) ? nextDayData.data.rows : [];
  const reviewTheoreticalRows: AgcStorageTheoreticalOptimalRow[] = reviewTheoreticalData?.data?.rows ?? [];
  const reviewWindowStart = `${dateStr}T06:00:00`;
  const reviewWindowEnd = `${nextDateStr}T06:00:00`;
  const reviewWindowLabel = `${dateStr} 06:00 - ${nextDateStr} 06:00`;
  const multiReviewRows = useMemo(
    () => buildReviewWindowRows(rows, dateStr, nextDayRows, nextDateStr),
    [rows, dateStr, nextDayRows, nextDateStr]
  );
  const multiReviewTheoreticalRows = useMemo(
    () => reviewTheoreticalRows,
    [reviewTheoreticalRows]
  );
  const multiReviewTotals = useMemo(
    () => calculateReviewActualTotals(multiReviewRows),
    [multiReviewRows]
  );
  const multiReviewTheoreticalSummary = useMemo(
    () => summarizeReviewTheoreticalRows(multiReviewTheoreticalRows),
    [multiReviewTheoreticalRows]
  );
  const theoreticalActiveRows = theoreticalRows.filter(
    (row) => toNumber(row.charge_power) > 0.001 || toNumber(row.discharge_power) > 0.001
  );
  const reviewStrategies = reviewStrategiesData?.data?.strategies ?? [];
  const exAnteStrategies = reviewStrategies.filter((strategy) => strategy.strategy_type === 'ex_ante_review');
  const intradayStrategies = reviewStrategies.filter((strategy) => strategy.strategy_type === 'intraday_dispatch');

  // Summary metrics
  const totals = useMemo(() => {
    if (!rows.length) {
      return {
        actualTotalRevenue: 0,
        strategyTotalRevenue: 0,
        passiveImpact: 0,
        actualSpreadRevenue: 0,
        strategySpreadRevenue: 0,
        declaredSpreadRevenue: 0,
        declaredDischargeRevenue: 0,
        declaredChargeCost: 0,
        strategyDischargeRevenue: 0,
        strategyChargeCost: 0,
        actualDischargeRevenue: 0,
        actualChargeCost: 0,
        totalCharge: 0,
        totalDischarge: 0,
        declaredCharge: 0,
        declaredDischarge: 0,
        strategyCharge: 0,
        strategyDischarge: 0,
        passiveCharge: 0,
        passiveDischarge: 0,
      };
    }
    let totalBaseRevenue = 0;
    let actualSpreadRevenue = 0;
    let strategySpreadRevenue = 0;
    let declaredSpreadRevenue = 0;
    let declaredDischargeRevenue = 0;
    let declaredChargeCost = 0;
    let strategyDischargeRevenue = 0;
    let strategyChargeCost = 0;
    let declaredCharge = 0;
    let declaredDischarge = 0;
    let passiveImpact = 0;
    let actualDischargeRevenue = 0;
    let actualChargeCost = 0;
    let totalCharge = 0;
    let totalDischarge = 0;
    let strategyCharge = 0;
    let strategyDischarge = 0;
    let passiveCharge = 0;
    let passiveDischarge = 0;
    for (const r of rows) {
      const actualSpread = toNumber(r.actual_spread_revenue, toNumber(r.e) - toNumber(r.c_q) * toNumber(r.p));
      const strategySpread = toNumber(r.strategy_spread_revenue, actualSpread);
      const ep = toNumber(r.ep);
      const price = toNumber(r.p);
      const fallbackDeclaredDischargeQ = Math.max(0, ep) * (5 / 60);
      const fallbackDeclaredChargeQ = Math.abs(Math.min(0, ep)) * (5 / 60);
      const fallbackDeclaredDischargeRevenue = fallbackDeclaredDischargeQ * price;
      const fallbackDeclaredChargeCost = fallbackDeclaredChargeQ * price;
      const declaredDischargeQ = toNumber(r.declared_discharge_q, fallbackDeclaredDischargeQ);
      const declaredChargeQ = toNumber(r.declared_charge_q, fallbackDeclaredChargeQ);
      const declaredDischargeRev = toNumber(r.declared_discharge_revenue, fallbackDeclaredDischargeRevenue);
      const declaredChargeCostValue = toNumber(r.declared_charge_cost, fallbackDeclaredChargeCost);
      totalBaseRevenue += toNumber(r.v) + toNumber(r.s) + toNumber(r.a);
      actualSpreadRevenue += actualSpread;
      strategySpreadRevenue += strategySpread;
      declaredSpreadRevenue += toNumber(r.declared_spread_revenue, declaredDischargeRev - declaredChargeCostValue);
      declaredDischargeRevenue += declaredDischargeRev;
      declaredChargeCost += declaredChargeCostValue;
      strategyDischargeRevenue += toNumber(r.strategy_discharge_revenue);
      strategyChargeCost += toNumber(r.strategy_charge_cost);
      passiveImpact += toNumber(r.passive_spread_impact, actualSpread - strategySpread);
      actualDischargeRevenue += toNumber(r.actual_discharge_revenue, toNumber(r.e));
      actualChargeCost += toNumber(r.actual_charge_cost, toNumber(r.c_q) * toNumber(r.p));
      totalCharge += toNumber(r.c_q);
      totalDischarge += toNumber(r.e_q);
      declaredCharge += declaredChargeQ;
      declaredDischarge += declaredDischargeQ;
      strategyCharge += toNumber(r.strategy_charge_q);
      strategyDischarge += toNumber(r.strategy_discharge_q);
      passiveCharge += toNumber(r.passive_charge_q);
      passiveDischarge += toNumber(r.passive_discharge_q);
    }
    return {
      actualTotalRevenue: totalBaseRevenue + actualSpreadRevenue,
      strategyTotalRevenue: totalBaseRevenue + strategySpreadRevenue,
      passiveImpact,
      actualSpreadRevenue,
      strategySpreadRevenue,
      declaredSpreadRevenue,
      declaredDischargeRevenue,
      declaredChargeCost,
      strategyDischargeRevenue,
      strategyChargeCost,
      actualDischargeRevenue,
      actualChargeCost,
      totalCharge,
      totalDischarge,
      declaredCharge,
      declaredDischarge,
      strategyCharge,
      strategyDischarge,
      passiveCharge,
      passiveDischarge,
    };
  }, [rows]);

  // Revenue breakdown
  const breakdown = useMemo(() => {
    if (!rows.length) {
      return { totalV: 0, totalS: 0, totalA: 0, avgVPrice: 0 };
    }
    let totalV = 0, totalS = 0, totalA = 0;
    for (const r of rows) {
      totalV += toNumber(r.v);
      totalS += toNumber(r.s);
      totalA += toNumber(r.a);
    }
    let vNum = 0, vDen = 0;
    for (const r of rows) {
      vNum += toNumber(r.v_q) * toNumber(r.v_p);
      vDen += toNumber(r.v_q);
    }
    const avgVPrice = vDen > 0 ? vNum / vDen : 0;
    return { totalV, totalS, totalA, avgVPrice };
  }, [rows]);

  const showZeroSubsidyWarning = subsidyPrice === 0 && hasData;
  const strategySummaryItems = useMemo(() => [
    {
      key: 'ex_ante_review',
      label: '事前复盘策略',
      strategy: latestReviewStrategy(exAnteStrategies),
      emptyNote: '每日交易日志还没有上传事前复盘策略',
    },
    {
      key: 'intraday_dispatch',
      label: '临场调度策略',
      strategy: latestReviewStrategy(intradayStrategies),
      emptyNote: '还缺 SOC 与日内判断形成的临场调度策略',
    },
  ], [exAnteStrategies, intradayStrategies]);

  const perspectiveCards = useMemo(() => [
    {
      title: '事后理论上限',
      value: multiReviewTheoreticalSummary ? `${format2(multiReviewTheoreticalSummary.netRevenue)} 元` : '待计算',
      note: multiReviewTheoreticalSummary
        ? `充/放 ${format2(multiReviewTheoreticalSummary.chargeEnergy)} / ${format2(multiReviewTheoreticalSummary.dischargeEnergy)} MWh`
        : '系统事后寻优上限',
    },
    {
      title: '事前复盘策略',
      ...summarizeReviewStrategies(exAnteStrategies, 'Codex 从每日交易日志抽取'),
    },
    {
      title: '临场调度策略',
      ...summarizeReviewStrategies(intradayStrategies, 'SOC 与日内判断人工输入'),
    },
    {
      title: '实际申报策略',
      value: multiReviewRows.length ? `${format2(multiReviewTotals.declaredSpreadRevenue)} 元` : '待计算',
      note: multiReviewRows.length ? `实际充/申报放 ${format2(multiReviewTotals.declaredCharge)} / ${format2(multiReviewTotals.declaredDischarge)} MWh` : '读取真实申报曲线',
    },
    {
      title: '实际物理收益',
      value: multiReviewRows.length ? `${format2(multiReviewTotals.actualSpreadRevenue)} 元` : '待计算',
      note: multiReviewRows.length ? `实际充/放 ${format2(multiReviewTotals.actualCharge)} / ${format2(multiReviewTotals.actualDischarge)} MWh` : '读取电池实际出力',
    },
  ], [
    exAnteStrategies,
    intradayStrategies,
    multiReviewRows.length,
    multiReviewTheoreticalSummary,
    multiReviewTotals.actualCharge,
    multiReviewTotals.actualDischarge,
    multiReviewTotals.actualSpreadRevenue,
    multiReviewTotals.declaredCharge,
    multiReviewTotals.declaredDischarge,
    multiReviewTotals.declaredSpreadRevenue,
  ]);

  const handleExportCsv = useCallback(() => {
    if (!rows.length) return;
    const headers = [
      'time', 'pv_avail', 'pv_act', 'agc_target', 'agc_act', 'ust', 'bat', 'ep', 'p',
      'v', 'v_q', 'e', 's', 'a', 'c_q', 'e_q', 'e_p', 'v_p',
      'declared_discharge_revenue', 'declared_charge_cost', 'declared_spread_revenue',
      'actual_discharge_revenue', 'actual_charge_cost', 'actual_spread_revenue',
      'strategy_bat', 'strategy_discharge_q', 'strategy_charge_q', 'strategy_spread_revenue',
      'passive_bat', 'passive_discharge_q', 'passive_charge_q', 'passive_spread_impact',
      'soc',
    ];
    type CsvRow = Record<string, string | number>;
    const lines = rows.map((r) => headers.map((h) => ((r as unknown) as CsvRow)[h] ?? '').join(','));
    const csv = '﻿' + [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizeFilename(selectedDisplay)}_${dateStr}_agc_storage测算.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [rows, selectedDisplay, dateStr]);

  if (stationsLoading) {
    return (
      <PageContainer title="AGC 配储精细化测算分析">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Spin size="large" tip="正在加载场站列表..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="AGC 配储精细化测算分析">
      {/* Control bar */}
      <div style={{ paddingBottom: 16, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>场站</div>
            <Select
              value={selectedDisplay}
              options={stations.map((d) => ({ value: d, label: d }))}
              onChange={setSelectedDisplay}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>业务日期</div>
            <DatePicker
              value={date}
              onChange={(v) => {
                if (v) setDate(v);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={5}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>参数</div>
            <Button onClick={() => setShowParams((p) => !p)} style={{ width: '100%' }}>
              参数设置
            </Button>
          </Col>
          <Col xs={12} sm={6} md={5}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
            <Button
              type="primary"
              icon={<CalculatorOutlined />}
              onClick={handleCalculate}
              loading={isPending}
              style={{ width: '100%' }}
            >
              计算最优策略
            </Button>
          </Col>
        </Row>

        {showParams && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={12} sm={6} md={4}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>考核单价</div>
              <InputNumber
                value={penaltyBase}
                onChange={(v) => setPenaltyBase(v ?? DEFAULT_PENALTY_BASE)}
                style={{ width: '100%' }}
                placeholder="考核单价"
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>综合转换效率</div>
              <InputNumber
                value={eff}
                onChange={(v) => setEff(v ?? DEFAULT_EFF)}
                style={{ width: '100%' }}
                step={0.01}
                min={0}
                max={1}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>弃光限电判定阈值 (MW)</div>
              <InputNumber
                value={curtThres}
                onChange={(v) => setCurtThres(v ?? DEFAULT_CURT_THRES)}
                style={{ width: '100%' }}
                step={0.1}
              />
            </Col>
          </Row>
        )}

        {showZeroSubsidyWarning && (
          <Alert
            message="该场站未配置 subsidy_price，Pdiff 将按 0 计算补贴电价基数。"
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </div>

      {/* Error state */}
      {error && (
        <Alert
          message={error instanceof Error ? error.message : '请求失败'}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Pdiff info bar — shown whenever we have a response */}
      {hasResponse && (
        <div style={{ marginBottom: 24, padding: '12px 16px', background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 10, fontSize: 13, color: '#c2410c' }}>
          当月光伏 MTD 均价: <strong>{format2(mtdAvg)}</strong> 元/MWh ｜
          推算 Pdiff: <strong>{format2(pdiff)}</strong> 元/MWh
          {capMaxDb > 0 && (
            <span> ｜ 数据库储能容量: <strong>{format2(capMaxDb)}</strong> MWh</span>
          )}
        </div>
      )}

      <Tabs
        defaultActiveKey="execution"
        items={[
          {
            key: 'execution',
            label: '策略执行情况',
            children: (
              <>
                {hasData && (
                  <div>
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                      <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>策略综合收益</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.strategyTotalRevenue)} 元</div>
                        </div>
                      </Col>
                      <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>实际综合收益</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.actualTotalRevenue)} 元</div>
                        </div>
                      </Col>
                      <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>偏差价差影响</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: totals.passiveImpact >= 0 ? '#16a34a' : '#dc2626' }}>{format2(totals.passiveImpact)} 元</div>
                        </div>
                      </Col>
                    </Row>

                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 8, textAlign: 'center' }}>
                      策略理论收益 {format2(totals.declaredSpreadRevenue)} 元（放电收入 {format2(totals.declaredDischargeRevenue)} / 充电成本 {format2(totals.declaredChargeCost)}） · 策略执行收益 {format2(totals.strategySpreadRevenue)} 元（放电收入 {format2(totals.strategyDischargeRevenue)} / 充电成本 {format2(totals.strategyChargeCost)}） · 实际收益 {format2(totals.actualSpreadRevenue)} 元（放电收入 {format2(totals.actualDischargeRevenue)} / 充电成本 {format2(totals.actualChargeCost)}）
                    </div>

                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 8, textAlign: 'center' }}>
                      虚报挽回 {format2(breakdown.totalV)} 元（均价 {format2(breakdown.avgVPrice)} 元/MWh） · 机制补贴 {format2(breakdown.totalS)} 元 · 规避考核 {format2(breakdown.totalA)} 元
                    </div>

                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 24, textAlign: 'center' }}>
                      申报充/放 {format2(totals.declaredCharge)} / {format2(totals.declaredDischarge)} MWh · 执行充/放 {format2(totals.strategyCharge)} / {format2(totals.strategyDischarge)} MWh · 实际充/放 {format2(totals.totalCharge)} / {format2(totals.totalDischarge)} MWh · 偏差充/放 {format2(totals.passiveCharge)} / {format2(totals.passiveDischarge)} MWh
                    </div>

                    <StorageDetailCharts
                      rows={rows}
                      loading={isPending}
                      error={error ?? null}
                      onRetry={handleCalculate}
                    />

                    <div style={{ marginTop: 24 }}>
                      <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCsv}>
                        导出测算数据 (CSV)
                      </Button>
                    </div>
                  </div>
                )}

                {hasResponse && !hasData && !isPending && !error && (
                  <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
                    <div style={{ fontSize: 16, color: '#f59e0b', marginBottom: 8 }}>该日期暂无对齐数据</div>
                    <div style={{ fontSize: 13 }}>请检查数据库或上传中心是否已上传该日数据</div>
                  </div>
                )}

                {!hasResponse && !isPending && !error && (
                  <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
                    选择场站、日期和参数后，点击「计算最优策略」查看配储测算结果
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'theoretical',
            label: '理论最优情况',
            children: (
              <div>
                {theoreticalError && (
                  <Alert
                    message={theoreticalError instanceof Error ? theoreticalError.message : '请求失败'}
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                  />
                )}
                {isTheoreticalPending && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                    <Spin size="large" tip="正在计算理论最优情况..." />
                  </div>
                )}
                {theoreticalSummary && !isTheoreticalPending && (
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                      <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>策略理论收益</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(theoreticalSummary.net_revenue)} 元</div>
                        </div>
                      </Col>
                      <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>实际最终收益</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.actualTotalRevenue)} 元</div>
                        </div>
                      </Col>
                      <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>理论-实际差额</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: theoreticalSummary.net_revenue - totals.actualTotalRevenue >= 0 ? '#16a34a' : '#dc2626' }}>
                            {format2(theoreticalSummary.net_revenue - totals.actualTotalRevenue)} 元
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 8, textAlign: 'center' }}>
                      <Tag color={theoreticalSummary.cycle_condition_met ? 'green' : 'red'}>{theoreticalSummary.message}</Tag>
                      价差 {format2(theoreticalSummary.spread)} 元/MWh · 门槛 {format2(theoreticalSummary.price_spread_threshold)} 元/MWh · 充电均价 {format2(theoreticalSummary.charge_avg_price)} · 放电均价 {format2(theoreticalSummary.discharge_avg_price)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 8, textAlign: 'center' }}>
                      充电 {format2(theoreticalSummary.charge_energy)} MWh（成本 {format2(theoreticalSummary.charge_cost)} 元） · 放电 {format2(theoreticalSummary.discharge_energy)} MWh（收入 {format2(theoreticalSummary.discharge_revenue)} 元）
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 24, textAlign: 'center' }}>
                      充电窗口 {formatWindow(theoreticalRows, 'charge_power')} · 放电窗口 {formatWindow(theoreticalRows, 'discharge_power')} · 有功点数 {theoreticalActiveRows.length}
                    </div>
                    <StorageStrategyCompareChart
                      actualRows={rows}
                      theoreticalRows={theoreticalRows}
                      loading={isPending || isTheoreticalPending}
                      error={(error as Error | null) ?? (theoreticalError as Error | null)}
                      onRetry={handleCalculate}
                    />
                  </>
                )}
                {theoreticalHasResponse && !theoreticalSummary && !isTheoreticalPending && !theoreticalError && (
                  <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
                    该日期暂无理论最优结果
                  </div>
                )}
                {!theoreticalHasResponse && !isTheoreticalPending && !theoreticalError && (
                  <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
                    点击「计算最优策略」后查看理论最优情况
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'multi-review',
            label: '多口径复盘',
            children: (
              <div>
                <Alert
                  message={`交易复盘窗口：${reviewWindowLabel}`}
                  description="仅多口径复盘使用 06:00 至次日 06:00；其它配储测算与收益测算仍按自然日财务结算口径。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                {(nextDayError || reviewTheoreticalError) && (
                  <Alert
                    message="次日 00:00-06:00 数据尚未完整，当前多口径复盘优先使用节点电价，缺失时临时使用实时临时电价。"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {reviewStrategiesError && (
                  <Alert
                    message={reviewStrategiesError instanceof Error ? reviewStrategiesError.message : '策略草稿加载失败'}
                    type="error"
                    showIcon
                    style={{ marginBottom: 20 }}
                  />
                )}

                <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                  {perspectiveCards.map((item) => (
                    <Col xs={24} sm={12} lg={8} xl={4} key={item.title}>
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 12px', minHeight: 104, background: '#fff' }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{item.title}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{item.value}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>{item.note}</div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div style={{ marginBottom: 20 }}>
                  <MultiPerspectiveReviewChart
                    actualRows={multiReviewRows}
                    theoreticalRows={multiReviewTheoreticalRows}
                    reviewStrategies={reviewStrategies}
                    targetDate={dateStr}
                    windowStart={reviewWindowStart}
                    windowEnd={reviewWindowEnd}
                    loading={isPending || isNextDayPending || isReviewTheoreticalPending || isReviewStrategiesLoading}
                    error={(error as Error | null) ?? (reviewTheoreticalError as Error | null) ?? (reviewStrategiesError as Error | null)}
                    onRetry={handleCalculate}
                  />
                </div>

                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>策略摘要</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                      上方卡片展示收益；这里仅保留当前采用的策略和待补缺口。
                    </div>
                  </div>
                  <Button size="small" onClick={() => refetchReviewStrategies()} loading={isReviewStrategiesLoading}>
                    刷新草稿
                  </Button>
                </div>

                {isReviewStrategiesLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 36 }}>
                    <Spin size="large" tip="正在加载策略草稿..." />
                  </div>
                )}

                {!isReviewStrategiesLoading && (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {strategySummaryItems.map((item) => {
                      const strategy = item.strategy;
                      if (!strategy) {
                        return (
                          <div key={item.key} style={{ border: '1px dashed #f59e0b', borderRadius: 8, padding: 14, background: '#fffbeb' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                              <Tag color="orange">{item.label}</Tag>
                              <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>还缺策略</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#92400e' }}>{item.emptyNote}</div>
                          </div>
                        );
                      }

                      const calculation = strategy.calculation;
                      const hasConcreteResult = hasConcreteReviewCalculation(strategy);
                      const missingItems = hasConcreteResult ? [] : strategyMissingItems(strategy);
                      return (
                        <div key={`${item.key}-${strategy.id}`} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, background: '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                            <div>
                              <Tag color="blue">{item.label}</Tag>
                              {calculation ? (
                                <Tag color={calculationStatusColor(calculation.status)}>{calculation.status_label}</Tag>
                              ) : (
                                <Tag color="orange">待计算</Tag>
                              )}
                              <span style={{ fontWeight: 700, color: '#374151' }}>
                                {strategy.strategy_name || `${strategy.date} ${strategyTypeLabel(strategy.strategy_type)}`}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              期初SOC {strategy.initial_soc_mwh === null ? '未提供' : `${format2(strategy.initial_soc_mwh)} MWh`}
                            </div>
                          </div>

                          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                            {strategyText(strategy)}
                            {strategy.carryover_soc !== null && strategy.carryover_soc !== undefined
                              ? `；跨日留电：${compactUnknown(strategy.carryover_soc, '')}`
                              : ''}
                          </div>

                          {!hasConcreteResult && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>还缺策略信息</span>
                              {missingItems.map((missing) => (
                                <Tag key={missing} color="red">{missing}</Tag>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </PageContainer>
  );
};

export default AgcStoragePage;
